import { configureStore } from '@reduxjs/toolkit';
import listenerMiddleware from './middleware';
import { DevTools } from './modules/App/components/DevTools';
import rootReducer from './reducers';
import type { RootState } from './reducers';
import { clearState, loadState } from './persistState';
import { loadUserCache } from './userCache';
import { getConfig } from './utils/getConfig';
import { parseBoolean } from './utils/parseStringToType';

// Enable DevTools only when rendering on client and during development.
// Display the dock monitor only if no browser extension is found.
export function showReduxDevTools() {
  return (
    parseBoolean(getConfig('CLIENT'), true) &&
    getConfig('NODE_ENV') === 'development' &&
    !window.__REDUX_DEVTOOLS_EXTENSION__
  );
}

export function setupStore(initialState: RootState) {
  const savedState = loadState();
  clearState();

  // Optimistically preload the last-known user + preferences so the UI renders real values on a cold
  // load instead of the signed-out/default state while Firebase auth hydrates. onAuthStateChanged
  // confirms (or clears) this shortly after. See client/userCache.ts.
  // `initialState` (window.__INITIAL_STATE__) is undefined in the static prod build, so guard with {}.
  // A partial preloadedState is fine — combineReducers fills missing slices from their initial state.
  const base = savedState || initialState || ({} as RootState);
  const cached = loadUserCache();
  const preloadedState = cached
    ? {
        ...base,
        // Show the cached identity immediately, but keep authHydrated FALSE: it's the "Firebase SDK
        // auth is ready for authorized reads" signal, which only the real onAuthStateChanged can set.
        // Data fetches gate on it so they don't fire (and fail) before auth.currentUser exists.
        user: { ...base?.user, ...cached.user, authHydrated: false },
        preferences: { ...base?.preferences, ...cached.preferences }
      }
    : base;

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: true,
        serializableCheck: false,
        // TODO: enable immutableCheck once the mutations are fixed.
        immutableCheck: false
      }).concat(listenerMiddleware.middleware),
    preloadedState,
    enhancers: showReduxDevTools() ? [DevTools.instrument()] : []
  });

  // Reducer hot-reloading is handled by Vite: editing a reducer triggers a full
  // module reload (React Fast Refresh preserves component state where it can).

  return store;
}
